import { DwQueryRequest } from '@views/CustomDashboards/types';
import { createExpressionArrayFromFilters } from '../utils';
import { FilterContext } from './types';
import { MitreColumnMap } from './utils';

// All agent list
export const getAllAgents = {
  select: {
    dims: [
      {
        name: 'agents.name',
        alias: 'name',
      },
      {
        name: 'agents.description',
        alias: 'description',
      },
      {
        name: 'agents._key',
        alias: 'agent_id',
      },
      {
        name: 'agents.category',
        alias: 'category',
      },
    ],
  },
  groupBy: ['agents.name', 'agents.description', 'agents._key'],
  filter: {
    and: {
      operands: [
        {
          expression: {
            fieldName: 'agents.is_enabled',
            stringCondition: {
              equals: 'true',
            },
          },
        },
        {
          expression: {
            fieldName: 'agents.agent_state',
            stringCondition: {
              equals: 'Production',
            },
          },
        },
      ],
    },
  },
  projectionId: {
    name: 'agents',
    builtIn: true,
  },
} as DwQueryRequest;

// Alerts count
export const getTotalAlertsCountByAgents = (filterContext: FilterContext) =>
  ({
    select: {
      metrics: [
        {
          name: 'count_distinct(alert.source_key)',
          alias: 'cnt',
        },
      ],
    },
    filter: {
      and: {
        operands: [
          {
            expression: {
              fieldName: 'agent_execution_data.input_type',
              stringCondition: {
                equals: 'Alert',
              },
            },
          },
          {
            expression: {
              fieldName: 'agents.is_enabled',
              stringCondition: {
                equals: 'true',
              },
            },
          },
          {
            expression: {
              fieldName: 'agents.agent_state',
              stringCondition: {
                equals: 'Production',
              },
            },
          },
          {
            expression: {
              fieldName: 'agent_execution_data.first_seen',
              dateCondition: { between: { values: filterContext.dateRange } },
            },
          },
          ...(filterContext?.typeMap ? createExpressionArrayFromFilters(filterContext) : []),
        ],
      },
    },
    projectionId: {
      name: 'agent_execution_data',
      builtIn: true,
    },
  }) as DwQueryRequest;

// total alerts by agents
export const getAlertsByAgent = (filterContext: FilterContext, agentId: string = '') =>
  ({
    select: {
      dims: [
        {
          name: 'agents.name',
          alias: 'name',
        },
        {
          name: 'agents.description',
          alias: 'description',
        },
        {
          name: 'agents._key',
          alias: 'agent_id',
        },
        {
          name: 'agents.category',
          alias: 'category',
        },
        {
          name: 'agents.outputs',
          alias: 'outcome',
        },
        {
          name: 'collect_distinct(agent_executions.PRIMARY_OUTCOME)',
          alias: 'primaryOutcome',
        },
        {
          name: 'collect_distinct(agent_executions.secondary_outcome)',
          alias: 'secondaryOutcome',
        },
        {
          name: 'agents.actions',
          alias: 'actions',
        },
      ],
      metrics: [
        {
          name: 'count_distinct(alert.source_key)',
          alias: 'cnt',
        },
      ],
    },
    groupBy: ['agents.category'],
    filter: {
      and: {
        operands: [
          {
            expression: {
              fieldName: 'agent_execution_data.input_type',
              stringCondition: {
                equals: 'Alert',
              },
            },
          },
          {
            expression: {
              fieldName: 'agents.is_enabled',
              stringCondition: {
                equals: 'true',
              },
            },
          },
          {
            expression: {
              fieldName: 'agents.agent_state',
              stringCondition: {
                equals: 'Production',
              },
            },
          },
          {
            expression: {
              fieldName: 'agent_execution_data.first_seen',
              dateCondition: { between: { values: filterContext.dateRange } },
            },
          },
          ...(filterContext?.typeMap ? createExpressionArrayFromFilters(filterContext) : []),
          ...(agentId
            ? [
                {
                  expression: {
                    fieldName: 'agents._key',
                    stringCondition: {
                      equals: agentId,
                    },
                  },
                },
              ]
            : []),
        ],
      },
    },
    projectionId: {
      name: 'agent_execution_data',
      builtIn: true,
    },
  }) as DwQueryRequest;

// alert count resolution under 5 min
export const getAlertCountUnder5Mins = (filterContext: FilterContext) =>
  ({
    select: {
      metrics: [
        {
          name: 'count_distinct(alert.source_key)',
          alias: 'cnt',
        },
      ],
    },
    filter: {
      and: {
        operands: [
          {
            expression: {
              fieldName: 'agent_execution_data.input_type',
              stringCondition: {
                equals: 'Alert',
              },
            },
          },
          {
            expression: {
              fieldName: 'agents.is_enabled',
              stringCondition: {
                equals: 'true',
              },
            },
          },
          {
            expression: {
              fieldName: 'agents.agent_state',
              stringCondition: {
                equals: 'Production',
              },
            },
          },
          {
            expression: {
              fieldName: 'agent_executions.latency',
              numberCondition: {
                lt: 30000,
              },
            },
          },
          {
            expression: {
              fieldName: 'agent_execution_data.first_seen',
              dateCondition: { between: { values: filterContext.dateRange } },
            },
          },
          ...createExpressionArrayFromFilters(filterContext),
        ],
      },
    },
    projectionId: {
      name: 'agent_execution_data',
      builtIn: true,
    },
  }) as DwQueryRequest;

// Alerts list by agent
export const getAlertsListByAgent = (filterContext: FilterContext, agentId: string | undefined, hCells) => {
  const columns = hCells
    .filter(item => !item.hidden)
    .map(item => ({ name: item.id }))
    .filter(item => !['mitre_tactics', 'mitre_techniques'].includes(item.name));
  return {
    select: {
      dims: [
        {
          name: 'distinct(alert._key)',
          alias: 'key',
        },
        MitreColumnMap.mitre_tactics,
        MitreColumnMap.mitre_techniques,
        ...columns,
      ],
      metrics: [
        {
          name: 'count_distinct(alert.source_key)',
          alias: 'cnt',
        },
      ],
    },
    groupBy: ['uber_alerts.agent_execution_data.agent_executions.agents._key', 'alert.source_key'],
    sorting: [],
    filter: {
      and: {
        operands: [
          {
            expression: {
              fieldName: 'uber_alerts.agent_execution_data.input_type',
              stringCondition: {
                equals: 'Alert',
              },
            },
          },
          {
            expression: {
              fieldName: 'uber_alerts.agent_execution_data.agent_executions.agents.is_enabled',
              stringCondition: {
                equals: 'true',
              },
            },
          },
          {
            expression: {
              fieldName: 'uber_alerts.agent_execution_data.agent_executions.agents.agent_state',
              stringCondition: {
                equals: 'Production',
              },
            },
          },
          {
            expression: {
              fieldName: 'uber_alerts.agent_execution_data.agent_executions.agents._key',
              stringCondition: {
                equals: agentId,
              },
            },
          },
          {
            expression: {
              fieldName: 'uber_alerts.agent_execution_data.first_seen',
              dateCondition: { between: { values: filterContext.dateRange } },
            },
          },
          ...createExpressionArrayFromFilters(filterContext),
        ],
      },
    },
    projectionId: {
      name: 'uber_alerts',
      builtIn: true,
    },
  } as DwQueryRequest;
};

// get human estimation saved
export const getAgentsHumanEstimation = (filterContext: FilterContext) => ({
  select: {
    dims: [
      {
        name: 'count(agent_execution_actions.human_estimate)',
        alias: 'human_estimate',
      },
      {
        name: 'count(agent_execution_actions.count)',
        alias: 'action_count',
      },
    ],
  },
  filter: {
    and: {
      operands: [
        {
          expression: {
            fieldName: 'agents.is_enabled',
            stringCondition: {
              equals: 'true',
            },
          },
        },
        {
          expression: {
            fieldName: 'agents.agent_state',
            stringCondition: {
              equals: 'Production',
            },
          },
        },
        {
          expression: {
            fieldName: 'agent_execution_actions.first_seen',
            dateCondition: { between: { values: filterContext.dateRange } },
          },
        },
        ...createExpressionArrayFromFilters(filterContext),
      ],
    },
  },
  projectionId: {
    name: 'agent_execution_actions',
    builtIn: true,
  },
});
